import React, { useState } from 'react'
import { getTokenOpenLogin } from 'syft-acp-core/entities/EmployerDetail/helpers'
import { currentIntl } from 'syft-acp-util/intl'
import { getSafeCurrencySymbol } from 'syft-acp-util/formatting'
import { getSkillTitles, getConflictDates } from './helpers'
import { Box } from '@indeed/ifl-components'
import S from './MatchingExplainability.module.scss'
import { SchemaProps, CommonDescriptionProps } from './types'

const CommonDescription = ({
  firstPart,
  linkPart,
  link,
  secondPart,
  result,
  onClick,
  additionalLinks,
}: CommonDescriptionProps) => {
  const [showAllLinks, setShowAllLinks] = useState(false)
  const handleOnClick = () => {
    window.open(link)
  }
  const handleAdditionalLinkOnClick = (additionalLink: string) => () => {
    window.open(additionalLink)
  }
  const handleDotsButton = () => {
    setShowAllLinks(true)
  }
  const links = showAllLinks || !additionalLinks ? additionalLinks : additionalLinks?.slice(0, 10)
  return (
    <Box as="div">
      {firstPart}{' '}
      <Box as="span" className={S['common-description__link']} onClick={onClick ? onClick : handleOnClick}>
        {linkPart}
      </Box>{' '}
      {secondPart} {result}
      {additionalLinks &&
        links &&
        links?.map((data: { link: string; label: string }, index: number) => {
          return (
            <>
              <Box
                as="span"
                className={S['common-description__link']}
                onClick={handleAdditionalLinkOnClick(data?.link)}
              >
                {data?.label}
              </Box>
              {index < links.length - 1 ? (
                ', '
              ) : links?.length < additionalLinks?.length ? (
                <Box as="span" className={S['common-description__link']} onClick={handleDotsButton}>
                  ... more
                </Box>
              ) : (
                ''
              )}
            </>
          )
        })}
    </Box>
  )
}

export const FILTERS_SCHEMA = [
  {
    title: 'Average rating',
    description: (data: SchemaProps) => {
      const actual = (data?.subRowData?.filterData?.extra_info?.actual ?? 0).toFixed(1)
      const threshold = (data?.subRowData?.filterData?.extra_info?.threshold ?? 0).toFixed(1)
      return (
        <CommonDescription
          firstPart="The"
          linkPart="worker"
          secondPart={`average rating of ${actual} is below the minimum rating of ${threshold} required for Let Flex Do The Work automatic offers`}
          result=""
          link={`/entity/workers/view/${data?.workerId}`}
        />
      )
    },
    tooltip:
      'This only affects Let Flex Do The Work automatic offers. Workers must have the minimum average rating to be included.',
    isUS: false,
    code: 'average_rating_filter',
  },
  {
    // Role
    title: 'Role Verification',
    description: (data: SchemaProps) => {
      const result = data?.roles?.[data?.subRowData?.filterData?.extra_info?.required]?.title
      return (
        <CommonDescription
          firstPart="The worker is"
          linkPart="not verified"
          secondPart="for"
          result={result}
          link={`/entity/workers/view/${data?.workerId}/interviews/list`}
        />
      )
    },
    tooltip: 'The worker will pass the filter if they are verified for the role required for the job',
    isUS: false,
    code: 'role_filter',
  },
  {
    // Skill
    title: 'Required skills',
    description: (data: SchemaProps) => {
      const result = getSkillTitles(
        data?.subRowData?.filterData?.extra_info?.required,
        data?.subRowData?.filterData?.extra_info?.actual,
        data,
      )
      return (
        <CommonDescription
          firstPart="The worker is missing the"
          linkPart="required"
          secondPart="skill(s) for the job:"
          result={result}
          link={`/entity/workers/view/${data?.workerId}/interviews/list`}
        />
      )
    },
    tooltip:
      'The worker will pass the filter if the worker has been verified for the role required by the job and has possessed all required skills (hard skills and essential soft skills) required by the job',
    isUS: false,
    code: 'skills_filter',
  },
  {
    // // Employer with listing is approved
    title: 'Blocked by venue',
    description: (data: SchemaProps) => {
      const tokenData = { id: data.employerID, approved_at: true }
      const onClick = () => {
        getTokenOpenLogin({
          actions: data.actions,
          data: tokenData,
          token: data.token,
          userData: data.userData,
          employerId: data.employerID,
          blockedWorkers: true,
        })
      }

      const result = `${data?.venues?.[data.venueID].name} ${data.venueID}`

      return (
        <CommonDescription
          firstPart="The worker is"
          linkPart="blocked"
          onClick={onClick}
          secondPart="by the following venue:"
          result={result}
          link={`/entity/employers/view/${data.employerID}/blocklist/list`}
        />
      )
    },
    tooltip: 'The worker will pass the filter if not blocked by the venue',
    isUS: false,
    code: 'blocked_at_work_location_filter',
  },
  {
    title: 'Blocked by client ',
    description: (data: SchemaProps) => {
      const employerData = `${data.employerName} (ID ${data.employerID})`
      const tokenData = { id: data.employerID, approved_at: true }
      const onClick = () => {
        getTokenOpenLogin({
          actions: data.actions,
          data: tokenData,
          token: data.token,
          userData: data.userData,
          employerId: data.employerID,
          blockedWorkers: true,
        })
      }
      return (
        <CommonDescription
          firstPart={`The worker is blocked by all venues for client ${employerData}. Please check the`}
          linkPart="blocklist"
          secondPart="on ACP and/or the"
          result={
            <>
              <Box as="span" className={S['common-description__link']} onClick={onClick}>
                blocked worker
              </Box>{' '}
              list on the client portal.
            </>
          }
          link={`/entity/employers/view/${data.employerID}/blocklist/list`}
        />
      )
    },
    tooltip: 'The worker will pass the filter if not blocked by the client',
    isUS: false,
    code: 'blocked_by_employer_filter',
  },
  {
    title: 'Availability - Worker profile setting & booked/offered status',
    description: (data: SchemaProps) => {
      const unavailableTimeslots = data?.subRowData?.filterData?.extra_info?.unavailable_timeslots
      const result = unavailableTimeslots?.join(', ')
      const resultConflict = data?.subRowData?.filterData?.extra_info?.booked_time_ranges
      const conflictingShiftIds = data?.subRowData?.filterData?.extra_info?.conflicting_shift_ids || []
      const withinTwoHours = data?.subRowData?.filterData?.extra_info?.within_two_hours_shift_ids || []
      const lateCancellationIds = data?.subRowData?.filterData?.extra_info?.late_cancellation_shift_ids || []
      const bookableIndividually = data?.subRowData?.filterData?.extra_info?.bookable_individually

      const workerShiftStatus = data?.subRowData?.filterData?.extra_info?.worker_shift_status || {
        offered_shift_ids: [],
        booked_shift_ids: [],
        approved_absence_shift_ids: [],
        cancelled_shift_ids: [],
      }

      const conflictLinks = conflictingShiftIds?.map((id: number) => ({
        label: `${id}`,
        link: `/shifts/all-shifts?&shift_id=${id}`,
      }))
      const withinTwoHoursLinksRaw = withinTwoHours?.map((id: number) => ({
        label: `${id}`,
        link: `/shifts/all-shifts?&shift_id=${id}`,
      }))
      const conflictingSet = new Set(conflictingShiftIds)
      const withinTwoHoursLinks = withinTwoHoursLinksRaw?.filter(
        (l: { label: string; link: string }) => !conflictingSet.has(Number(l.label)),
      )

      const { startDate, endDate } = getConflictDates(resultConflict)
      const workerShiftsLink = `/shifts/all-shifts?&worker_id=${data?.workerId}&start_time_gte=${startDate}&start_time_lte=${endDate}&exclude_bookings=false&disable_pagination_counters=true`

      const messageBlocks: React.ReactNode[] = []

      if (workerShiftStatus.offered_shift_ids.length > 0) {
        const offeredLinks = workerShiftStatus.offered_shift_ids.map((id: number) => ({
          label: `${id}`,
          link: `/shifts/all-shifts?&shift_id=${id}`,
        }))
        const count = offeredLinks.length
        const countLabel = `${count} shift${count === 1 ? '' : 's'}:`
        messageBlocks.push(
          <CommonDescription
            firstPart={`Worker has been offered — ${countLabel}`}
            linkPart=""
            secondPart=""
            result=""
            link={`/entity/workers/view/${data?.workerId}`}
            additionalLinks={offeredLinks}
          />,
        )
      }

      if (workerShiftStatus.booked_shift_ids.length > 0) {
        const bookedLinks = workerShiftStatus.booked_shift_ids.map((id: number) => ({
          label: `${id}`,
          link: `/shifts/all-shifts?&shift_id=${id}`,
        }))
        const count = bookedLinks.length
        const countLabel = `${count} shift${count === 1 ? '' : 's'}:`
        messageBlocks.push(
          <CommonDescription
            firstPart={`Worker is booked — ${countLabel}`}
            linkPart=""
            secondPart=""
            result=""
            link={`/entity/workers/view/${data?.workerId}`}
            additionalLinks={bookedLinks}
          />,
        )
      }

      if (workerShiftStatus.approved_absence_shift_ids.length > 0) {
        const absenceLinks = workerShiftStatus.approved_absence_shift_ids.map((id: number) => ({
          label: `${id}`,
          link: `/shifts/all-shifts?&shift_id=${id}`,
        }))
        const count = absenceLinks.length
        const countLabel = `${count} shift${count === 1 ? '' : 's'}:`
        messageBlocks.push(
          <CommonDescription
            firstPart={`Worker has approved absence — ${countLabel}`}
            linkPart=""
            secondPart=""
            result=""
            link={`/entity/workers/view/${data?.workerId}`}
            additionalLinks={absenceLinks}
          />,
        )
      }

      if (workerShiftStatus.cancelled_shift_ids.length > 0) {
        const cancelledLinks = workerShiftStatus.cancelled_shift_ids.map((id: number) => ({
          label: `${id}`,
          link: `/shifts/all-shifts?&shift_id=${id}`,
        }))
        const count = cancelledLinks.length
        const countLabel = `${count} shift${count === 1 ? '' : 's'}:`
        messageBlocks.push(
          <CommonDescription
            firstPart={`Worker cancelled shifts — ${countLabel}`}
            linkPart=""
            secondPart=""
            result=""
            link={`/entity/workers/view/${data?.workerId}`}
            additionalLinks={cancelledLinks}
          />,
        )
      }

      if (conflictingShiftIds.length > 0) {
        const conflictsCount = conflictLinks?.length || 0
        const countLabel = `${conflictsCount} shift${conflictsCount === 1 ? '' : 's'}:`
        messageBlocks.push(
          <CommonDescription
            firstPart={`Overlaps with existing bookings — ${countLabel}`}
            linkPart=""
            secondPart=""
            result=""
            link={workerShiftsLink}
            additionalLinks={conflictLinks}
          />,
        )
      }

      if (withinTwoHoursLinks && withinTwoHoursLinks.length > 0) {
        const withinCount = withinTwoHoursLinks.length
        const countLabel = `${withinCount} shift${withinCount === 1 ? '' : 's'}:`
        messageBlocks.push(
          <CommonDescription
            firstPart={`Shifts within the required 2-hour gap — ${countLabel}`}
            linkPart=""
            secondPart=""
            result=""
            link={workerShiftsLink}
            additionalLinks={withinTwoHoursLinks}
          />,
        )
      }

      if (lateCancellationIds.length > 0) {
        const lateLinks = lateCancellationIds.map((id: number) => ({
          label: `${id}`,
          link: `/shifts/all-shifts?&shift_id=${id}`,
        }))
        const count = lateLinks.length
        messageBlocks.push(
          <CommonDescription
            firstPart={`Worker has ${count} late-cancelled shift${
              count === 1 ? '' : 's'
            } with active penalty periods —`}
            linkPart=""
            secondPart=""
            result=""
            link={workerShiftsLink}
            additionalLinks={lateLinks}
          />,
        )
      }

      if (unavailableTimeslots && unavailableTimeslots.length > 0) {
        messageBlocks.push(
          <CommonDescription
            firstPart="The worker’s"
            linkPart="profile availability"
            secondPart="excludes:"
            result={result}
            link={`/entity/workers/view/${data?.workerId}`}
          />,
        )
      }

      const hasConflictData =
        conflictingShiftIds.length > 0 ||
        withinTwoHours.length > 0 ||
        lateCancellationIds.length > 0 ||
        (unavailableTimeslots && unavailableTimeslots.length > 0)

      if (hasConflictData && bookableIndividually === true) {
        messageBlocks.push(
          <CommonDescription
            firstPart="This job is individually bookable — at least one shift must meet booking rules: it must not overlap another booking and there must be a minimum 2‑hour gap after any booking, including any shift the worker late‑cancelled that hasn't ended yet. In this check, all evaluated shifts fail these rules."
            linkPart=""
            secondPart=""
            result=""
            link={workerShiftsLink}
          />,
        )
      } else if (hasConflictData && bookableIndividually === false) {
        messageBlocks.push(
          <CommonDescription
            firstPart="This job is not individually bookable — all shifts must meet booking rules: they must not overlap another booking and there must be a minimum 2‑hour gap after any booking, including any shift the worker late‑cancelled that hasn't ended yet. At least one shift fails these rules."
            linkPart=""
            secondPart=""
            result=""
            link={workerShiftsLink}
          />,
        )
      }

      if (messageBlocks.length === 0) {
        messageBlocks.push(<>The availability filter failed but no specific reason could be determined.</>)
      }

      return <>{messageBlocks}</>
    },
    tooltip:
      'A worker passes this availability check when shifts meet booking rules: no overlaps with existing bookings and at least a 2‑hour gap after any booking. Shifts the worker late‑cancelled that haven’t ended yet also count as overlaps. For individually bookable jobs, at least one shift must meet these rules; for non‑individually bookable jobs, all shifts must meet them.',
    isUS: false,
    code: 'availability_filter',
  },
  {
    title: 'Federated state ',
    tooltip:
      'The worker will pass the filter if their federated state is the exact match to the federated state of the venue it is located in',
    isUS: true,
    description: (data: SchemaProps) => {
      const required = data?.subRowData?.filterData?.extra_info?.required
      const actual = data?.subRowData?.filterData?.extra_info?.actual
      return (
        <CommonDescription
          firstPart="The worker’s set up to work in"
          linkPart=""
          secondPart={`${actual}, but the venue is located in`}
          result={required}
          link={`/entity/workers/view/${data?.workerId}`}
        />
      )
    },
    code: 'federated_state_filter',
  },
  {
    title: 'City',
    tooltip:
      'The worker will pass the filter if their city info is an exact match to the city the venue is located in',
    isUS: true,
    description: (data: SchemaProps) => {
      const link = data?.subRowData?.filterData?.extra_info?.actual
        ?.map((cityId: number) => data?.cities?.[cityId]?.name)
        .join(', ')
        .replace(/, ([^,]*)$/, ' and $1')
      const result = data?.cities?.[data?.subRowData?.filterData?.extra_info?.required]?.name
      return (
        <CommonDescription
          firstPart="The worker’s set up to work in"
          linkPart={`${link},`}
          secondPart="but the venue is located in"
          result={result}
          link={`/entity/workers/view/${data?.workerId}`}
        />
      )
    },
    code: 'city_filter',
  },
  {
    title: 'Pay rate - Worker profile setting',
    description: (data: SchemaProps) => {
      const result = `${getSafeCurrencySymbol(
        currentIntl.currencyCode,
      )}${data?.subRowData?.filterData?.extra_info?.minimum_rate.toFixed(2)}`
      return (
        <CommonDescription
          firstPart="The worker’s"
          linkPart="preferred minimum pay rate"
          secondPart="is"
          result={result}
          link={`/entity/workers/view/${data?.workerId}`}
        />
      )
    },
    tooltip:
      'The worker will pass the filter if the client’s pay rate is greater or equal to the worker’s minimum pay rate specified as a preference in-app',
    isUS: false,
    code: 'minimum_rate_filter',
  },
  {
    title: 'Distance to Venue',
    description: (data: SchemaProps) => {
      return (
        <CommonDescription
          firstPart="The worker’s"
          linkPart="preferred travel distance"
          secondPart="is < than the distance to the venue."
          result=""
          link={`/entity/workers/view/${data?.workerId}`}
        />
      )
    },
    tooltip:
      'Workers will pass this filter check if the distance to venue is ≤ the preferred travel distance. (Only applicable to non-remote jobs)',
    isUS: false,
    code: 'distance_filter',
  },
  {
    title: 'Dormant worker',
    tooltip: 'Workers will pass this filter check if they are not dormant (not active within 90 days).',
    description: (data: SchemaProps) => {
      const result = data?.subRowData?.filterData?.extra_info?.last_active_at
      return (
        <CommonDescription
          firstPart="The worker has not been"
          linkPart="active"
          secondPart="since"
          result={result}
          link={`/entity/workers/view/${data?.workerId}`}
        />
      )
    },
    isUS: false,
    code: 'activity_v2_filter',
  },
  {
    title: 'Worker working hour limit',
    description: (data: SchemaProps) => {
      return (
        <CommonDescription
          firstPart="The worker has reached their"
          linkPart=" working hour limit"
          secondPart="for the week."
          result=""
          link={`/entity/workers/view/${data?.workerId}`}
        />
      )
    },
    tooltip:
      'The worker will pass the filter if their weekly booked hours have not exceeded their weekly working hour limit',
    isUS: false,
    code: 'weekly_hours_filter',
  },
  {
    title: 'Overtime Prevention',
    description: (data: SchemaProps) => {
      const error = data?.subRowData?.filterData?.extra_info?.error
      const totalHours = error?.total_hours_and_minutes
      const weekStart = error?.week_start
      const weekEnd = error?.week_end

      return (
        <CommonDescription
          firstPart="This client enforces a 40 hour weekly limit to prevent overtime. This "
          linkPart="worker"
          secondPart={` is booked on ${totalHours} from ${weekStart} - ${weekEnd}`}
          result=""
          link={`/entity/workers/view/${data?.workerId}`}
        />
      )
    },
    tooltip:
      'The worker will pass this filter if booking the job does not exceed their weekly working hour limit of 40 hours',
    isUS: true,
    code: 'overtime_prevention_filter',
  },
  {
    title: 'Working Time Directive',
    description: (data: SchemaProps) => {
      const extraInfo = data?.subRowData?.filterData?.extra_info
      const errors = extraInfo?.errors || []
      const shiftsWithinRestPeriod = extraInfo?.shifts_within_rest_period || []

      const errorLinks = errors.map((err: { shift_id: number; error_description: string }) => ({
        label: `${err.shift_id}`,
        link: `/shifts/all-shifts?&shift_id=${err.shift_id}`,
      }))
      const restPeriodLinks = shiftsWithinRestPeriod.map(
        (s: { shift_id: number; duration_hours: number }) => ({
          label: `${s.shift_id} (${s.duration_hours}h)`,
          link: `/shifts/all-shifts?&shift_id=${s.shift_id}`,
        }),
      )

      const messageBlocks: React.ReactNode[] = []

      if (errors.length > 0) {
        const firstError = errors[0]
        const countLabel =
          errorLinks.length > 0 ? ` — ${errorLinks.length} shift${errorLinks.length === 1 ? '' : 's'}: ` : ''
        messageBlocks.push(
          <CommonDescription
            firstPart={`${firstError.error_description}${countLabel}`}
            linkPart=""
            secondPart=""
            result=""
            link={`/entity/workers/view/${data?.workerId}`}
            additionalLinks={errorLinks}
          />,
        )
      }

      if (shiftsWithinRestPeriod.length > 0) {
        const restCount = shiftsWithinRestPeriod.length
        const restCountLabel = `${restCount} shift${restCount === 1 ? '' : 's'}: `
        messageBlocks.push(
          <CommonDescription
            firstPart={`Existing shift(s) within the required 11-hour rest period — ${restCountLabel}`}
            linkPart=""
            secondPart=""
            result=""
            link={`/entity/workers/view/${data?.workerId}`}
            additionalLinks={restPeriodLinks}
          />,
        )
      }

      if (messageBlocks.length === 0) {
        messageBlocks.push(
          <>
            Taking the job would violate Working Time Directive rest period requirements (11h rest between
            shifts).
          </>,
        )
      }

      return <>{messageBlocks}</>
    },
    tooltip:
      'Does taking the job violate Working Time Directive checks. These checks include weekly hour limit, daily working limit, maximum shift duration and resting period between shifts. (Will not block offering the job)',
    isUS: false,
    code: 'working_time_directive_filter',
  },
  {
    title: 'Weekly Rest',
    description: (data: SchemaProps) => {
      const description = data?.subRowData?.filterData?.description
      return (
        <CommonDescription
          firstPart="The"
          linkPart="worker"
          secondPart={`${description}`}
          result=""
          link={`/entity/workers/view/${data?.workerId}`}
        />
      )
    },
    tooltip:
      "A worker must have 24 consecutive hours of rest in 7 days, or they must have either 2x24 or 1x48 consecutive hours of rest in a fortnight.  Any given week is part of two fortnights so if that week doesn't have enough rest then both fortnights must be compliant.",
    isUS: false,
    code: 'weekly_rest_filter',
  },
]
